import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEnterSubmit } from "@/utils/hooks/enter-submit.hooks";
import { useAnalytics } from "@/utils/hooks/analytics.hooks";
import { useAtomValue } from "jotai";
import { userSettingsAtom } from "@/atoms/user-settings";
import { usePrivatePageContext } from "@/utils/hooks/private-page.hooks";
import { i18nError, UnauthorizedError } from "@/@types/api-error";
import { toApiError } from "@/utils/api-client";
import { useChat } from "ai/react";
import { useScrollAnchor } from "@/utils/hooks/scroll-anchor.hooks";

export const useDocumentChat = (documentId: string, chatId: string) => {
  const { t } = useTranslation();
  const { logout } = usePrivatePageContext();
  const { sendDocumentChatEvent } = useAnalytics();
  const { language } = useAtomValue(userSettingsAtom);
  const [recentRallyLimit, setRecentRallyLimit] = useState<string>("");
  const [apiErrorDialogOpen, setApiErrorDialogOpen] = useState<boolean>(false);
  const [apiErrorMessage, setApiErrorMessage] = useState<string>("");
  const { scrollAnchorRef, scrollToAnchor } = useScrollAnchor();

  const isNotIosDevice = useMemo(() => {
    const userAgent = navigator.userAgent;
    return !(userAgent.includes("iPhone") || userAgent.includes("iPad") || userAgent.includes("iPod"));
  }, []);

  const { messages, append, isLoading, input, setInput } = useChat({
    id: chatId,
    api: `/api/documents/${documentId}/chats`,
    body: {
      language,
      id: chatId,
    },
    onResponse: (response) => {
      if (response.status >= 400) throw toApiError(response.status, response.headers.get("x-trace-id") ?? "");
      setRecentRallyLimit(response.headers.get("X-Recent-Rally-Limit") ?? "");
    },
    onError: (error: Error) => {
      if (error instanceof UnauthorizedError) {
        logout();
        return;
      }

      if (error instanceof i18nError) {
        setApiErrorMessage(t(error.message));
      } else {
        setApiErrorMessage(t("api_error"));
      }
      setApiErrorDialogOpen(true);

      setInput(input); // NOTE: ユーザが入力した内容を入力欄に戻す
    },
  });

  const onSubmit = useCallback(() => {
    if (isLoading) return;
    const content = input.trim();
    if (!content) return;
    sendDocumentChatEvent();

    append({ id: documentId, role: "user", content });
    setInput("");
  }, [append, documentId, input, isLoading, sendDocumentChatEvent, setInput]);

  const { onKeyDown } = useEnterSubmit(onSubmit);

  const inputPlaceholder = useMemo(() => {
    if (!recentRallyLimit || recentRallyLimit === "0") return t("document_chat_input_placeholder");
    if (messages.length <= 1 || (messages.length === 2 && isLoading)) return t("document_chat_input_placeholder");
    return t("chat_input_placeholder_second");
  }, [isLoading, messages.length, recentRallyLimit, t]);

  return {
    t,
    isNotIosDevice,
    scrollAnchorRef,
    scrollToAnchor,
    inputPlaceholder,
    recentRallyLimit,
    apiErrorMessage,
    apiErrorDialogOpen,
    setApiErrorDialogOpen,
    onSubmit,
    onKeyDown,
    messages,
    isLoading,
    input,
    setInput,
  };
};
